import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Check, Eye, Monitor, Smartphone, Layers } from 'lucide-react';
import { templates } from '../data/templates';

export default function TemplateDetail() {
  const { id } = useParams();
  const template = templates.find(t => t.id === id);

  if (!template) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1816] mb-4">Template not found</h1>
          <Link to="/templates" className="text-[#C9A96E] underline">Back to Templates</Link>
        </div>
      </div>
    );
  }

  const isLive = template.status === 'live';

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <header className="py-4 px-6 lg:px-12 max-w-[1400px] mx-auto flex items-center justify-between">
        <Link to="/templates" className="flex items-center gap-2 text-[#1A1816] hover:text-[#C9A96E] transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-xs font-semibold tracking-wider uppercase">All Templates</span>
        </Link>
        <Link to="/" className="text-lg font-bold tracking-tight">
          <span className="font-sans text-[#1A1816]">HORIZON</span>
          <span className="text-[#C9A96E] text-sm ml-1 tracking-[0.15em]">DIGITAL</span>
        </Link>
      </header>

      {/* Animated bg */}
      <div className="fixed inset-0 pointer-events-none select-none overflow-hidden z-0">
        <motion.div className="absolute whitespace-nowrap" style={{ top: '20%' }}
          animate={{ x: ['5%', '-35%'] }} transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}>
          <span className="text-[16vw] font-bold text-[#1A1816]/[0.015] tracking-[-0.05em] font-sans leading-none">
            {template.name.toUpperCase()}&nbsp;&nbsp;&nbsp;{template.industry.toUpperCase()}&nbsp;&nbsp;&nbsp;{template.name.toUpperCase()}
          </span>
        </motion.div>
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 pt-8 pb-20">
        {/* Top info */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left — Preview area */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="relative aspect-[4/3] bg-[#F0EDE8] border border-[#1A1816]/5 overflow-hidden">
              {template.thumb ? (
                <img src={template.thumb} alt={template.name} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${template.color}20, ${template.color}08)` }}>
                  <div className="text-center">
                    <span className="text-8xl font-serif italic" style={{ color: template.color + '25' }}>{template.name[0]}</span>
                    <p className="text-sm font-semibold tracking-wider uppercase mt-3" style={{ color: template.color + '70' }}>{template.name}</p>
                    <p className="text-xs mt-1" style={{ color: template.color + '50' }}>{template.industry}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Live preview button */}
            {isLive && (
              <Link to={`/templates/${template.id}/preview`} target="_blank"
                className="mt-4 w-full flex items-center justify-center gap-2 bg-[#1A1816] text-[#FAF8F5] py-4 text-xs font-semibold tracking-wider uppercase hover:bg-[#C9A96E] hover:text-[#1A1816] transition-all duration-500">
                <Eye className="w-4 h-4" /> View Live Preview
              </Link>
            )}
            {!isLive && (
              <div className="mt-4 w-full flex items-center justify-center gap-2 bg-[#8A857D]/20 text-[#8A857D] py-4 text-xs font-semibold tracking-wider uppercase cursor-not-allowed">
                Coming Soon
              </div>
            )}
          </motion.div>

          {/* Right — Details */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <span className="text-[9px] font-semibold tracking-[0.3em] uppercase text-[#C9A96E]">{template.industry}</span>
            <h1 className="text-3xl sm:text-4xl font-sans font-bold text-[#1A1816] mt-2 mb-1">{template.name}</h1>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-4xl font-serif italic text-[#C9A96E]">{template.price}</span>
              <span className="text-xs text-[#8A857D]">starting price</span>
            </div>

            <p className="text-sm text-[#8A857D] leading-relaxed mb-8">{template.description}</p>

            {/* Features */}
            <div className="mb-8">
              <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A1816] mb-4">Sections Included</h2>
              <div className="space-y-2.5">
                {template.sections.map((s) => (
                  <div key={s} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-[#C9A96E] flex-shrink-0" />
                    <span className="text-sm text-[#1A1816]/70">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tech */}
            <div className="mb-8">
              <h2 className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A1816] mb-4">Built With</h2>
              <div className="flex flex-wrap gap-2">
                {template.tech.map((t) => (
                  <span key={t} className="px-3 py-1.5 border border-[#1A1816]/10 text-[10px] font-semibold tracking-wider uppercase text-[#8A857D]">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { icon: Monitor, label: 'Desktop Ready' },
                { icon: Smartphone, label: 'Mobile Responsive' },
                { icon: Layers, label: 'Clean Code' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-2 p-4 border border-[#1A1816]/5">
                  <Icon className="w-5 h-5 text-[#C9A96E]" />
                  <span className="text-[9px] font-semibold tracking-wider uppercase text-[#8A857D]">{label}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <Link to="/#contact"
              className="w-full flex items-center justify-center gap-2 bg-[#C9A96E] text-[#1A1816] py-4 text-xs font-semibold tracking-wider uppercase hover:bg-[#1A1816] hover:text-[#FAF8F5] transition-all duration-500">
              Request This Website <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
